import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ClipboardList, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { API } from "@/context/AuthContext";
import { inrShort } from "@/pages/Dashboard";

export function ItemStockLoader({ onDone }) {
  const [open, setOpen] = useState(false);
  const [snaps, setSnaps] = useState([]);
  const [file, setFile] = useState(null);
  const [asOf, setAsOf] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { try { setSnaps((await axios.get(`${API}/inventory/item-stock`)).data.snapshots || []); } catch { setSnaps([]); } }, []);
  useEffect(() => { if (open) load(); }, [open, load]);
  const upload = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = (await axios.post(`${API}/inventory/item-stock/upload`, fd, { params: asOf ? { as_of: asOf } : {} })).data;
      toast.success(`Item-wise closing stock ${r.as_of} loaded: ${r.row_count.toLocaleString()} rows · ${inrShort(r.value)}${r.by_special?.O ? ` · Job work ${inrShort(r.by_special.O)}` : ""}`);
      setFile(null); load(); onDone?.(r.as_of);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  const remove = async (d) => {
    try { await axios.delete(`${API}/inventory/item-stock`, { params: { as_of: d } }); toast.success(`Removed ${d}`); load(); onDone?.(d); }
    catch { toast.error("Could not remove"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" className="border-slate-300" data-testid="ageing-itemstock-btn"><ClipboardList className="h-4 w-4 mr-1.5" />Item-wise closing stock</Button></DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="ageing-itemstock-dialog">
        <DialogHeader><DialogTitle>Item-wise closing stock (SAP valuation)</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Upload the SAP item-wise stock export for a month-end (Material · Plant · Storage Location · Special Stock Type · WBS · Supplier · Unrestricted Stock · Value). Stock Ageing for that date then uses SAP's own quantities and values — Job work (O), Project (Q) and own stock tie to the rupee — and shows the subcontractor holding each item.</p>
        <div className="rounded-md border border-slate-200 p-3 space-y-2">
          <div className="flex items-end gap-2 flex-wrap">
            <div><label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">File (.xlsx / .csv)</label>
              <Input type="file" accept=".xlsx,.xlsm,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="h-9 w-64 border-slate-300 text-xs" data-testid="ageing-itemstock-file" /></div>
            <div><label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">As of (optional)</label>
              <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="h-9 w-40 border-slate-300 text-sm" data-testid="ageing-itemstock-asof" title="Leave blank to take the file's Reporting Date" /></div>
            <Button size="sm" onClick={upload} disabled={!file || busy} data-testid="ageing-itemstock-upload"><Upload className="h-4 w-4 mr-1" />{busy ? "Uploading…" : "Upload"}</Button>
          </div>
          <p className="text-[11px] text-slate-400">Leave "As of" blank when the file carries a Reporting Date column. Re-uploading the same date replaces it.</p>
        </div>
        <div className="space-y-1" data-testid="ageing-itemstock-list">
          <p className="text-xs font-semibold text-slate-800">Loaded month-ends</p>
          {snaps.length === 0 && <p className="text-xs text-slate-400">None yet — Stock Ageing falls back to MB5B / SAP prices.</p>}
          {snaps.map((s) => (
            <div key={s.as_of} className="flex items-center justify-between rounded-md border border-slate-100 px-2 py-1.5 text-xs" data-testid={`ageing-itemstock-${s.as_of}`}>
              <span><b className="font-mono">{s.as_of}</b> · {s.row_count.toLocaleString()} rows · {inrShort(s.value)} <span className="text-slate-400">· {s.filename}</span></span>
              <button type="button" onClick={() => remove(s.as_of)} className="text-slate-400 hover:text-red-600" title="Remove" data-testid={`ageing-itemstock-del-${s.as_of}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
